<template lang="pug">
  .add-modal
    .add-field-modal
      .map-form
        GmapMap.custom-map(
          ref="mapRef"
          :center="center"
          :zoom="zoom"
          :tilt=0
          map-type-id="roadmap"
        )
        br
        button.toggle-legend-btn(@click="showLegend = !showLegend")
          | {{ showLegend ? '❌ Hide Legend' : 'ℹ️ Show Legend on map' }}
        .map-legend(v-if="showLegend")
          h4 How to Create a Field Polygon
          ul
            li 🔵 Click on the map to place a pin (max 10)
            li 🟢 Drag a pin to reposition it
            li 🟥 Right-click a pin to remove it
            li 🔺 Green area: your editable field polygon
            li 🔶 Colored areas: other fields from the selected site
      .add-form
        el-form(
          :model="form"
          ref="AddFieldForm"
          :rules="addFieldFormRules"
          @submit.native.prevent
        )
          el-row(:gutter="24")
            el-col(:span="12")
              el-form-item(
                prop="name"
                :label="$t('fields.actions.label.name')"
              )
                el-input(
                  v-model="form.name"
                  name="name"
                )
            el-col(:span="12")
              el-form-item(
                prop="site_id"
                :label="$t('fields.actions.label.site')"
              )
                el-select.w-100(
                  v-model='form.site_id'
                  filterable
                  allow-create
                  :placeholder="$t('fields.actions.label.site')"
                )
                  el-option(
                    v-for='site in sites'
                    :key='site.id'
                    :label='site.name'
                    :value='site.id'
                  )
          el-row(:gutter="24")
            el-col(:span="24")
              el-form-item(
                prop="name"
                :label="$t('fields.actions.label.color')"
              )
              color-picker-with-presets(v-model="color")
    el-row.text-center
      el-button.dark-green-btn.pop-up-btn(
        @click="handleAddNewField('AddFieldForm')"
        :loading="loading"
      ) {{ $t('common.add') }}

</template>

<script>
import { gmapApi } from "vue2-google-maps";
import { mapActions, mapMutations, mapState } from "vuex";
import { getRomaniaCoordsIfLocationIsOff } from "@utils/map";
import { customValidations } from "@utils/customValidators";
import { fieldsViewValidations } from "@utils/formValidations/fieldsViewValidations";
import { setObjectValues } from "@utils/generalUtils";
import ColorPickerWithPresets from "@components/_shared/ColorPickerWithPresets.vue";

export default {
  name: "AddField",
  mixins: [fieldsViewValidations, customValidations],

  components: {
    'color-picker-with-presets': ColorPickerWithPresets,
  },

  data() {
    return {
      showLegend: true,
      marker: [],
      markers: [],
      zoom: 10,
      center: { lat: 0, lng: 0},
      form: {
        name: null,
        site_id: null
      },

      sites: [],
      fields: [],
      fieldPolygons: [],
      fieldsListPolygons: [],

      loading: false,
      polygoneCoords: [],
      color: { hex: '#00FF00' },

      setObjectValues,
    };
  },

  watch: {
    'form.site_id': {
      async handler(newValue, oldValue) {
        if (oldValue !== newValue) {
          await this.handleGetFields();
          this.drawFieldsListPolygons();
        }
      }
    },

    'color.hex'(newColor) {
      this.updatePolygonColor(newColor);
    },
  },

  computed: {
    google: gmapApi,

    ...mapState("auth", ["user"]),
  },

  async created() {
    await this.getBrowserLocation();
    await this.handleGetSites();
    await this.handleGetFields();
    this.drawFieldsListPolygons();
  },

  async mounted() {
    this.initializeFieldPolygon();
  },

  methods: {
    ...mapActions("site", ["getSites"]),
    ...mapActions('field', ['getFields', 'addField']),
    ...mapMutations("dialog", ["hideAddFieldDialog"]),
    ...mapMutations("field", [
      "setField",
      "setShouldFetchFields",
    ]),

    async handleGetSites() {
      const sites = await this.getSites("?page_size=1000");
      this.sites = sites.data;
    },

    async handleGetFields() {
      const fields = await this.getFields(`?page_size=1000&by_site_id=${this.form.site_id}`);
      this.fields = fields.data;
    },


    handleAddNewField(formName) {
      const form = this.$refs[formName];

      form.validate(async (valid) => {
        if (valid) {
          this.polygoneCoords = this.markers.map(marker => ({
            lat: marker.getPosition().lat(),
            lng: marker.getPosition().lng(),
            latitude: marker.getPosition().lat(),
            longitude: marker.getPosition().lng(),
          }));

          let payload = {
            name: this.form.name,
            color: this.color.hex.replace('#', ''),
            site_id: this.form.site_id,
            field_coordinates: this.polygoneCoords,
          };

          this.addField(payload)
            .then(() => {
              this.hideAddFieldDialog();
              this.clearForm();
              this.setShouldFetchFields(true);
            })
            .finally(() => {
              this.loading = false;
            });
        } else {
          this.loading = false;
        }
      });
    },

    clearForm() {
      this.setObjectValues(this.form, "");
    },

    drawFieldsListPolygons() {
      var fieldsListPolygoneCoords = [];

      this.fieldsListPolygons.map((polygon) => polygon.setMap(null))
            
      this.$refs.mapRef.$mapPromise.then((map) => {
        this.fields.forEach((field) => {
          if (!field || !Array.isArray(field.field_coordinates)) return;

          fieldsListPolygoneCoords = [];

          field.field_coordinates.forEach((field_coordinate) => {
            fieldsListPolygoneCoords.push({
              lat: Number(field_coordinate.latitude),
              lng: Number(field_coordinate.longitude),
            });
          });

          const myPolygon = new google.maps.Polygon({
            paths: fieldsListPolygoneCoords,
            map: map,
            strokeColor: `#${field.color}`,
            strokeOpacity: 0.3,
            strokeWeight: 3,
            fillColor: `#${field.color}`,
            fillOpacity: 0.5,
          });

          this.fieldsListPolygons.push(myPolygon);
        });
      });
    },

    async initializeFieldPolygon() {
      const map = await this.$refs.mapRef.$mapPromise;
      map.addListener("click", (event) => {
        if (this.markers.length >= 10) {
          return this.$message({
            type: "warning",
            message: "Maximum of 10 markers allowed.",
          });
        }

        const marker = new google.maps.Marker({
          position: event.latLng,
          map,
          icon: this.pinSymbol(this.color.hex),
          draggable: true,
        });

        this.attachMarkerEvents(marker);
        this.markers.push(marker);

        if (this.markers.length >= 3) {
          this.drawPolygonFromMarkers();
        }
      });
    },

    attachMarkerEvents(marker) {
      marker.addListener("rightclick", () => {
        if (this.markers.length <= 3) {
          return this.$message({
            type: "error",
            message: "At least 3 points are required for a valid area.",
          });
        }

        marker.setMap(null);
        this.markers = this.markers.filter(m => m !== marker);
        this.drawPolygonFromMarkers(); // auto-update polygon
      });

      marker.addListener("dragend", () => {
        this.drawPolygonFromMarkers(); // auto-update polygon
      });
    },

    drawPolygonFromMarkers() {
      this.clearPolygons();

      if (this.markers.length < 3) return;

      const map = this.markers[0].getMap();
      const coords = this.markers.map(m => {
        const pos = m.getPosition();
        return { lat: pos.lat(), lng: pos.lng() };
      });

      const polygon = new google.maps.Polygon({
        paths: coords,
        map,
        strokeColor: this.color.hex,
        strokeOpacity: 0.8,
        strokeWeight: 2,
        fillColor: this.color.hex,
        fillOpacity: 0.35,
      });

      this.fieldPolygons.push(polygon);
    },

    clearPolygons() {
      this.fieldPolygons.forEach(p => p.setMap(null));
      this.fieldPolygons = [];
    },

    pinSymbol(color = "green") {
      return {
        path: google.maps.SymbolPath.CIRCLE,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: '#000',
        strokeWeight: 2,
        scale: 8
      };
    },

    updatePolygonColor(newColor) {
      this.fieldPolygons.forEach(polygon => {
        polygon.setOptions({
          strokeColor: newColor,
          fillColor: newColor,
        });
      });

      this.markers.forEach(marker => {
        marker.setIcon(this.pinSymbol(newColor));
      });
    },

    async getBrowserLocation() {
      navigator.geolocation.getCurrentPosition((location) => {
        this.center.lat = location.coords.latitude;
        this.center.lng = location.coords.longitude;
      });

      // if location is not working or turned off
      const romaniaCoords = await getRomaniaCoordsIfLocationIsOff()
      if (romaniaCoords) {
        this.center.lat = romaniaCoords.coords.latitude
        this.center.lng = romaniaCoords.coords.longitude
        this.zoom = 7
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@variables";

.add-form {
  margin-left: 20px;
  .el-tabs__nav-scroll {
    display: flex;
    justify-content: center;
  }
}

.add-field-modal {
  display: grid;
  grid-template-columns: 4fr 2fr;
  gap: 5px;
  padding: 5px;
}

.map-legend {
  position: absolute;
  margin-top: -215px;
  left: 50px;
  max-width: 290px;
  background: white;
  border: 1px solid #ccc;
  padding: 8px 10px;
  font-size: 12px;
  z-index: 5;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.2);
  line-height: 1.4;
  word-wrap: break-word;
}

.map-legend h4 {
  margin: 0 0 6px;
  font-size: 13px;
  font-weight: bold;
}

.map-legend ul {
  list-style: none;
  padding-left: 0;
  margin: 0;
}

.map-legend li {
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.3;
}

.toggle-legend-btn {
  z-index: 6;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
}

.custom-map {
  width: 100% !important;
  // height: 60vh !important;
  height: 400px !important;
}
</style>
